import React, { useCallback, useState } from "react";
import "./PlayerToggles.css";

/**
 * Две кнопки-переключателя для нижней панели плеера (VideoControls), справа от громкости:
 *   1) всплывающие комментарии — показывать ли плашки при проходе таймкода;
 *   2) автоскролл — прокручивать ли список комментариев к текущему.
 *
 * Иконки — в стиле иконок панели (32×32, обводка 1.5, #E8F2FF). Выключенное состояние —
 * приглушённая иконка с диагональной чертой, как у «звук выключен».
 *
 *   <PlayerToggles
 *     toastsEnabled={toastsEnabled}  onToastsChange={setToastsEnabled}
 *     autoScroll={autoScroll}        onAutoScrollChange={setAutoScroll}
 *   />
 *
 * Состояние хранится снаружи; usePersistedToggle ниже — готовый стейт с памятью в localStorage.
 */
export default function PlayerToggles({
  toastsEnabled = true,
  onToastsChange,
  autoScroll = true,
  onAutoScrollChange,
  labels = {
    toastsOn: "Всплывающие комментарии: вкл",
    toastsOff: "Всплывающие комментарии: выкл",
    scrollOn: "Автоскролл к комментарию: вкл",
    scrollOff: "Автоскролл к комментарию: выкл",
  },
  className = "",
}) {
  return (
    <div className={`ptg ${className}`}>
      <button
        type="button"
        className={`control-btn ptg__btn ${toastsEnabled ? "" : "ptg__btn--off"}`}
        aria-pressed={toastsEnabled}
        title={toastsEnabled ? labels.toastsOn : labels.toastsOff}
        onClick={() => onToastsChange?.(!toastsEnabled)}
      >
        <IconToasts />
        {!toastsEnabled && <IconSlash />}
      </button>
      <button
        type="button"
        className={`control-btn ptg__btn ${autoScroll ? "" : "ptg__btn--off"}`}
        aria-pressed={autoScroll}
        title={autoScroll ? labels.scrollOn : labels.scrollOff}
        onClick={() => onAutoScrollChange?.(!autoScroll)}
      >
        <IconAutoScroll />
        {!autoScroll && <IconSlash />}
      </button>
    </div>
  );
}

/** Стейт с памятью в localStorage (настройка зрителя, не данные). */
export function usePersistedToggle(key, defaultValue = true) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw == null ? defaultValue : raw === "1";
    } catch (_) {
      return defaultValue;
    }
  });
  const set = useCallback(
    (next) => {
      setValue(next);
      try { window.localStorage.setItem(key, next ? "1" : "0"); } catch (_) { /* приватный режим */ }
    },
    [key]
  );
  return [value, set];
}

const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round" };

/** Пузырь комментария с хвостиком — во весь бокс 32×32, как иконки панели. */
function IconToasts() {
  return (
    <svg className="ptg__icon" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
      <path d="M8 5.5h16a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9.5L9 27.5v-5A4 4 0 0 1 4 18.5v-9a4 4 0 0 1 4-4Z" {...stroke} />
      <path d="M10 11.5h12M10 16.5h7" {...stroke} />
    </svg>
  );
}

/** Список и стрелка вниз «следовать за текущим» — во весь бокс. */
function IconAutoScroll() {
  return (
    <svg className="ptg__icon" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
      <path d="M4 7h13M4 13h13M4 19h9M4 25h9" {...stroke} />
      <path d="M24.5 6v20M19.5 21l5 5 5-5" {...stroke} />
    </svg>
  );
}

/** Диагональная черта для выключенного состояния. */
function IconSlash() {
  return (
    <svg className="ptg__slash" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
      <path d="M6 26 26 6" stroke="#181c24" strokeWidth="5" strokeLinecap="round" />
      <path d="M6 26 26 6" {...stroke} />
    </svg>
  );
}
